import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Truck, User, Users, Car, FileText, CheckCircle, 
  ArrowLeft, ArrowRight, Plus, Trash2, Eraser, Upload,
  Building, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { id: 1, title: 'Owner Details', icon: User },
  { id: 2, title: 'Bank Details', icon: CreditCard },
  { id: 3, title: 'Next of Kin', icon: Users },
  { id: 4, title: 'Vehicles/Drivers', icon: Car },
  { id: 5, title: 'Documents', icon: FileText },
  { id: 6, title: 'Agreements', icon: CheckCircle },
];

export default function NewDriverRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Form data with ALL fields
  const [formData, setFormData] = useState({
    // Owner details
    owner_full_name: '',
    owner_id_number: '',
    owner_contact_number: '',
    owner_address: '',
    company_trading_as: '',
    // Bank details
    bank_name: '',
    account_number: '',
    branch_code: '',
    account_type: '',
    // Next of kin
    nok_name: '',
    nok_relationship: '',
    nok_contact: '',
    // Vehicles with extended driver info
    vehicles: [{
      vehicle_make_model: '',
      vehicle_registration: '',
      vehicle_type: '',
      vehicle_comments: '',
      driver_full_name: '',
      driver_id_number: '',
      driver_address: '',
      driver_contact_number: '',
      driver_license: '',
      license_disc: '',
      pdp: '',
      criminal_checks: ''
    }],
    // Finance comments
    finance_comments: '',
    general_comments: '',
    // Agreements
    criminal_check_consent: false,
    valid_pdp_required: false,
    insurance_contribution_accept: false,
    tracking_device_active: false,
    responsibility_acknowledgement: false,
    weekly_records_acknowledgement: false,
  });

  // Documents state
  const [documents, setDocuments] = useState({});

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const updateVehicle = (index, field, value) => {
    const newVehicles = [...formData.vehicles];
    newVehicles[index] = { ...newVehicles[index], [field]: value };
    setFormData({ ...formData, vehicles: newVehicles });
  };

  const addVehicle = () => {
    setFormData({
      ...formData,
      vehicles: [...formData.vehicles, {
        vehicle_make_model: '',
        vehicle_registration: '',
        vehicle_type: '',
        vehicle_comments: '',
        driver_full_name: '',
        driver_id_number: '',
        driver_address: '',
        driver_contact_number: '',
        driver_license: '',
        license_disc: '',
        pdp: '',
        criminal_checks: ''
      }]
    });
  };

  const removeVehicle = (index) => {
    if (formData.vehicles.length > 1) {
      const newVehicles = formData.vehicles.filter((_, i) => i !== index);
      setFormData({ ...formData, vehicles: newVehicles });
    }
  };

  const handleDocumentUpload = (docType, file) => {
    setDocuments({ ...documents, [docType]: file });
  };

  // Signature functions
  const initCanvas = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = 200;
      ctx.strokeStyle = '#0F172A';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1: // Owner Details
        if (!formData.owner_full_name || !formData.owner_id_number || 
            !formData.owner_contact_number || !formData.owner_address) {
          toast.error('Please fill in all required owner details');
          return false;
        }
        if (formData.owner_id_number.length !== 13) {
          toast.error('ID number must be 13 digits');
          return false;
        }
        break;
      case 2: // Bank Details
        if (!formData.bank_name || !formData.account_number || !formData.branch_code) {
          toast.error('Please fill in all required bank details');
          return false;
        }
        break;
      case 3: // Next of Kin
        if (!formData.nok_name || !formData.nok_relationship || !formData.nok_contact) {
          toast.error('Please fill in all next of kin details');
          return false;
        }
        break;
      case 4: // Vehicles
        for (const vehicle of formData.vehicles) {
          if (!vehicle.vehicle_make_model || !vehicle.vehicle_registration ||
              !vehicle.driver_full_name || !vehicle.driver_id_number ||
              !vehicle.driver_address || !vehicle.driver_contact_number) {
            toast.error('Please fill in all required vehicle and driver details');
            return false;
          }
        }
        break;
      case 6: // Agreements
        if (!formData.criminal_check_consent || !formData.valid_pdp_required ||
            !formData.insurance_contribution_accept || !formData.tracking_device_active ||
            !formData.responsibility_acknowledgement || !formData.weekly_records_acknowledgement) {
          toast.error('Please accept all agreements');
          return false;
        }
        if (!hasSignature) {
          toast.error('Please provide your signature');
          return false;
        }
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep === STEPS.length) {
        handleSubmit();
      } else {
        setCurrentStep(currentStep + 1);
        if (currentStep + 1 === STEPS.length) {
          setTimeout(initCanvas, 100);
        }
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Get signature data
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      
      // Submit registration
      const response = await axios.post(`${API_URL}/registrations`, {
        ...formData,
        signature_data: signatureData
      });
      
      const regId = response.data.id;
      setRegistrationId(regId);
      
      // Upload documents
      for (const [docType, file] of Object.entries(documents)) {
        if (file) {
          const docFormData = new FormData();
          docFormData.append('doc_type', docType);
          docFormData.append('file', file);
          
          await axios.post(`${API_URL}/registrations/${regId}/documents`, docFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit registration'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for registering with JSM. Your application is being reviewed. 
              You will be contacted once your registration is approved.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Reference: <span className="font-mono font-medium">{registrationId}</span>
            </p>
            <Link to="/login">
              <Button className="w-full">Return to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="font-bold text-xl">JSM</span>
          </div>
          <Link to="/login" className="text-sm hover:text-orange-400">
            Already registered? Sign in
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center justify-between min-w-[600px]">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`
                    flex flex-col items-center
                    ${isActive ? 'text-orange-600' : isCompleted ? 'text-green-600' : 'text-muted-foreground'}
                  `}>
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center mb-2
                      ${isActive ? 'bg-orange-100' : isCompleted ? 'bg-green-100' : 'bg-muted'}
                    `}>
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`
                      w-8 sm:w-16 h-1 mx-2
                      ${step.id < currentStep ? 'bg-green-500' : 'bg-muted'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">New Driver Registration</CardTitle>
            <CardDescription>
              Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Step 1: Owner Details */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="owner_full_name">OWNER NAME *</Label>
                    <Input
                      id="owner_full_name"
                      value={formData.owner_full_name}
                      onChange={(e) => updateFormData('owner_full_name', e.target.value)}
                      placeholder="Enter your full name"
                      data-testid="owner-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_id_number">OWNER ID NUMBER *</Label>
                    <Input
                      id="owner_id_number"
                      value={formData.owner_id_number}
                      onChange={(e) => updateFormData('owner_id_number', e.target.value.replace(/\D/g, '').slice(0, 13))}
                      placeholder="13-digit ID number"
                      data-testid="owner-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_contact_number">OWNER CELL *</Label>
                    <Input
                      id="owner_contact_number"
                      value={formData.owner_contact_number}
                      onChange={(e) => updateFormData('owner_contact_number', e.target.value)}
                      placeholder="e.g., 082 123 4567"
                      data-testid="owner-contact-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_trading_as">COMPANY TRADING AS</Label>
                    <Input
                      id="company_trading_as"
                      value={formData.company_trading_as}
                      onChange={(e) => updateFormData('company_trading_as', e.target.value)}
                      placeholder="Trading name (if applicable)"
                      data-testid="company-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="owner_address">OWNER ADDRESS *</Label>
                    <Input
                      id="owner_address"
                      value={formData.owner_address}
                      onChange={(e) => updateFormData('owner_address', e.target.value)}
                      placeholder="Enter your full residential address"
                      data-testid="owner-address-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Bank Details */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Please provide your banking details for payment processing.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">BANK NAME *</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => updateFormData('bank_name', e.target.value)}
                      placeholder="e.g., FNB, ABSA, Standard Bank, Nedbank"
                      data-testid="bank-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account_number">ACCOUNT NUMBER *</Label>
                    <Input
                      id="account_number"
                      value={formData.account_number}
                      onChange={(e) => updateFormData('account_number', e.target.value)}
                      placeholder="Your bank account number"
                      data-testid="account-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="branch_code">BRANCH CODE *</Label>
                    <Input
                      id="branch_code"
                      value={formData.branch_code}
                      onChange={(e) => updateFormData('branch_code', e.target.value)}
                      placeholder="e.g., 250655"
                      data-testid="branch-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account_type">ACCOUNT TYPE *</Label>
                    <Input
                      id="account_type"
                      value={formData.account_type}
                      onChange={(e) => updateFormData('account_type', e.target.value)}
                      placeholder="e.g., Cheque, Savings, Transmission"
                      data-testid="account-type-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Next of Kin */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Please provide emergency contact details.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nok_name">Full Name *</Label>
                    <Input
                      id="nok_name"
                      value={formData.nok_name}
                      onChange={(e) => updateFormData('nok_name', e.target.value)}
                      placeholder="Next of kin's full name"
                      data-testid="nok-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nok_relationship">Relationship *</Label>
                    <Input
                      id="nok_relationship"
                      value={formData.nok_relationship}
                      onChange={(e) => updateFormData('nok_relationship', e.target.value)}
                      placeholder="e.g., Spouse, Parent, Sibling"
                      data-testid="nok-relationship-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nok_contact">Contact Number *</Label>
                    <Input
                      id="nok_contact"
                      value={formData.nok_contact}
                      onChange={(e) => updateFormData('nok_contact', e.target.value)}
                      placeholder="e.g., 082 123 4567"
                      data-testid="nok-contact-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Vehicles & Drivers */}
            {currentStep === 4 && (
              <div className="space-y-6">
                {formData.vehicles.map((vehicle, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-lg">Vehicle & Driver {index + 1}</h3>
                      {formData.vehicles.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVehicle(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    
                    {/* Vehicle Details */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                        <Car className="h-4 w-4" /> Vehicle Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>VEHICLE TYPE *</Label>
                          <Input
                            value={vehicle.vehicle_make_model}
                            onChange={(e) => updateVehicle(index, 'vehicle_make_model', e.target.value)}
                            placeholder="e.g., Toyota Hilux 2.4GD"
                            data-testid={`vehicle-${index}-make-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>REGISTRATION NUMBER *</Label>
                          <Input
                            value={vehicle.vehicle_registration}
                            onChange={(e) => updateVehicle(index, 'vehicle_registration', e.target.value)}
                            placeholder="e.g., CA 123-456"
                            data-testid={`vehicle-${index}-reg-input`}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>VEHICLE COMMENTS</Label>
                          <Input
                            value={vehicle.vehicle_comments}
                            onChange={(e) => updateVehicle(index, 'vehicle_comments', e.target.value)}
                            placeholder="Any notes about the vehicle (color, condition, etc.)"
                            data-testid={`vehicle-${index}-comments-input`}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    {/* Driver Details */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                        <User className="h-4 w-4" /> Driver Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>DRIVER NAME *</Label>
                          <Input
                            value={vehicle.driver_full_name}
                            onChange={(e) => updateVehicle(index, 'driver_full_name', e.target.value)}
                            placeholder="Driver's full name"
                            data-testid={`vehicle-${index}-driver-name-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>DRIVER ID NUMBER *</Label>
                          <Input
                            value={vehicle.driver_id_number}
                            onChange={(e) => updateVehicle(index, 'driver_id_number', e.target.value.replace(/\D/g, '').slice(0, 13))}
                            placeholder="13-digit ID number"
                            data-testid={`vehicle-${index}-driver-id-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>DRIVER CONTACT NUMBER *</Label>
                          <Input
                            value={vehicle.driver_contact_number}
                            onChange={(e) => updateVehicle(index, 'driver_contact_number', e.target.value)}
                            placeholder="e.g., 082 123 4567"
                            data-testid={`vehicle-${index}-driver-contact-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>DRIVER ADDRESS *</Label>
                          <Input
                            value={vehicle.driver_address}
                            onChange={(e) => updateVehicle(index, 'driver_address', e.target.value)}
                            placeholder="Driver's residential address"
                            data-testid={`vehicle-${index}-driver-address-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>DRIVERS LICENSE</Label>
                          <Input
                            value={vehicle.driver_license}
                            onChange={(e) => updateVehicle(index, 'driver_license', e.target.value)}
                            placeholder="License number"
                            data-testid={`vehicle-${index}-license-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>LICENSE DISC</Label>
                          <Input
                            value={vehicle.license_disc}
                            onChange={(e) => updateVehicle(index, 'license_disc', e.target.value)}
                            placeholder="License disc status/expiry"
                            data-testid={`vehicle-${index}-disc-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>PDP (Professional Driving Permit)</Label>
                          <Input
                            value={vehicle.pdp}
                            onChange={(e) => updateVehicle(index, 'pdp', e.target.value)}
                            placeholder="PDP number/status"
                            data-testid={`vehicle-${index}-pdp-input`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>CRIMINAL CHECKS</Label>
                          <Input
                            value={vehicle.criminal_checks}
                            onChange={(e) => updateVehicle(index, 'criminal_checks', e.target.value)}
                            placeholder="Status (e.g., Clear, Pending)"
                            data-testid={`vehicle-${index}-criminal-input`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={addVehicle}
                  className="w-full"
                  data-testid="add-vehicle-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Vehicle & Driver
                </Button>
                
                {/* Finance Comments */}
                <div className="border rounded-lg p-4 bg-slate-50 mt-6">
                  <h4 className="text-sm font-medium text-slate-600 mb-3">Additional Comments</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>FINANCE COMMENTS</Label>
                      <Textarea
                        value={formData.finance_comments}
                        onChange={(e) => updateFormData('finance_comments', e.target.value)}
                        placeholder="Any finance-related notes..."
                        rows={2}
                        data-testid="finance-comments-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>GENERAL COMMENTS</Label>
                      <Textarea
                        value={formData.general_comments}
                        onChange={(e) => updateFormData('general_comments', e.target.value)}
                        placeholder="Any other information you'd like to share..."
                        rows={2}
                        data-testid="general-comments-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Documents */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Upload the required documents and photos. All files should be clear and legible.
                </p>
                
                {/* Owner Photo & Documents */}
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <h3 className="font-semibold mb-2 text-blue-800">Owner Photo</h3>
                  <p className="text-xs text-blue-600 mb-4">Photo of owner from waist area upwards (passport style)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUpload
                      label="Owner Photo (Waist Up)"
                      docType="owner_photo"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.owner_photo}
                      accept="image/*"
                    />
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-slate-50">
                  <h3 className="font-semibold mb-4">Owner Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUpload
                      label="ID Document (Copy)"
                      docType="owner_id_doc"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.owner_id_doc}
                    />
                    <DocumentUpload
                      label="Driver's License"
                      docType="driver_license"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.driver_license}
                    />
                    <DocumentUpload
                      label="Proof of Address"
                      docType="proof_of_address"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.proof_of_address}
                    />
                    <DocumentUpload
                      label="Proof of Bank Account"
                      docType="proof_of_bank_account"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.proof_of_bank_account}
                    />
                    <DocumentUpload
                      label="Bank Confirmation Letter"
                      docType="bank_confirmation_letter"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.bank_confirmation_letter}
                    />
                  </div>
                </div>

                {/* Driver Photo */}
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <h3 className="font-semibold mb-2 text-green-800">Driver & Assistant Photos</h3>
                  <p className="text-xs text-green-600 mb-4">Photos from waist area upwards (passport style)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUpload
                      label="Driver Photo (Waist Up)"
                      docType="driver_photo"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.driver_photo}
                      accept="image/*"
                    />
                    <DocumentUpload
                      label="Assistant Photo (Waist Up)"
                      docType="assistant_photo"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.assistant_photo}
                      accept="image/*"
                    />
                  </div>
                </div>

                {/* Vehicle Photos */}
                <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                  <h3 className="font-semibold mb-2 text-orange-800">Vehicle Photos</h3>
                  <p className="text-xs text-orange-600 mb-4">Upload clear photos of your vehicle from all angles</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUpload
                      label="Vehicle FRONT (Show Numberplate)"
                      docType="vehicle_photo_front"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.vehicle_photo_front}
                      accept="image/*"
                    />
                    <DocumentUpload
                      label="Vehicle LEFT SIDE"
                      docType="vehicle_photo_left"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.vehicle_photo_left}
                      accept="image/*"
                    />
                    <DocumentUpload
                      label="Vehicle RIGHT SIDE"
                      docType="vehicle_photo_right"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.vehicle_photo_right}
                      accept="image/*"
                    />
                    <DocumentUpload
                      label="Vehicle BACK (Show Canopy)"
                      docType="vehicle_photo_back"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.vehicle_photo_back}
                      accept="image/*"
                    />
                  </div>
                </div>

                {/* Other Vehicle Documents */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h3 className="font-semibold mb-4">Vehicle & Driver Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUpload
                      label="Tracker Certificate"
                      docType="tracker_certificate"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.tracker_certificate}
                    />
                    <DocumentUpload
                      label="Criminal Check"
                      docType="criminal_check"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.criminal_check}
                    />
                    <DocumentUpload
                      label="PDP Document"
                      docType="driver_pdp"
                      onUpload={handleDocumentUpload}
                      uploaded={!!documents.driver_pdp}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Agreements */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h3 className="font-semibold mb-4">Declarations & Agreements</h3>
                  <div className="space-y-4">
                    <AgreementCheckbox
                      id="criminal_check_consent"
                      checked={formData.criminal_check_consent}
                      onChange={(checked) => updateFormData('criminal_check_consent', checked)}
                      label="I consent to a criminal background check being conducted"
                    />
                    <AgreementCheckbox
                      id="valid_pdp_required"
                      checked={formData.valid_pdp_required}
                      onChange={(checked) => updateFormData('valid_pdp_required', checked)}
                      label="I confirm that I have a valid Professional Driving Permit (PDP)"
                    />
                    <AgreementCheckbox
                      id="insurance_contribution_accept"
                      checked={formData.insurance_contribution_accept}
                      onChange={(checked) => updateFormData('insurance_contribution_accept', checked)}
                      label="I accept the monthly insurance contribution of R399 per vehicle"
                    />
                    <AgreementCheckbox
                      id="tracking_device_active"
                      checked={formData.tracking_device_active}
                      onChange={(checked) => updateFormData('tracking_device_active', checked)}
                      label="I confirm that the tracking device is active on my vehicle(s)"
                    />
                    <AgreementCheckbox
                      id="responsibility_acknowledgement"
                      checked={formData.responsibility_acknowledgement}
                      onChange={(checked) => updateFormData('responsibility_acknowledgement', checked)}
                      label="I acknowledge my responsibility for the vehicle and any damages caused by negligence"
                    />
                    <AgreementCheckbox
                      id="weekly_records_acknowledgement"
                      checked={formData.weekly_records_acknowledgement}
                      onChange={(checked) => updateFormData('weekly_records_acknowledgement', checked)}
                      label="I agree to maintain accurate weekly records of all trips and deliveries"
                    />
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-slate-50">
                  <h3 className="font-semibold mb-2">Digital Signature</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    By signing below, I confirm that all information provided is true and accurate.
                  </p>
                  <div className="signature-pad border-2 border-dashed rounded-lg overflow-hidden">
                    <canvas
                      ref={canvasRef}
                      className="w-full h-[200px] cursor-crosshair bg-white"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      data-testid="registration-signature-canvas"
                    />
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button variant="outline" size="sm" onClick={clearSignature}>
                      <Eraser className="h-4 w-4 mr-2" />
                      Clear Signature
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                data-testid="prev-step-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              <Button
                onClick={nextStep}
                disabled={submitting}
                data-testid="next-step-button"
              >
                {submitting ? (
                  'Submitting...'
                ) : currentStep === STEPS.length ? (
                  <>
                    Submit Registration
                    <CheckCircle className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Document Upload Component
const DocumentUpload = ({ label, docType, onUpload, uploaded, accept = ".pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx,.doc,.docx" }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    if (e.target.files?.[0]) {
      onUpload(docType, e.target.files[0]);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div
        className={`file-upload-area border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-slate-100 transition-colors ${uploaded ? 'border-green-500 bg-green-50' : 'border-slate-300'}`}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="hidden"
          data-testid={`doc-${docType}-input`}
        />
        {uploaded ? (
          <div className="flex items-center justify-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Uploaded</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Upload className="h-6 w-6" />
            <span className="text-sm">Click to upload</span>
            <span className="text-xs text-slate-400">PDF, Image, CSV, Excel</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Agreement Checkbox Component
const AgreementCheckbox = ({ id, checked, onChange, label }) => (
  <div className="flex items-start gap-3">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={onChange}
      data-testid={`agreement-${id}`}
    />
    <Label htmlFor={id} className="text-sm leading-relaxed cursor-pointer">
      {label}
    </Label>
  </div>
);
